import type { TouchEvent as ReactTouchEvent } from 'react';
import type { DraggedMail } from './clean';

/*
 * Touch drag of a mail row (iPad / phone), where HTML5 drag-and-drop does not
 * work: press and hold a row, then slide the finger onto a drop target. Targets
 * are elements with `data-mail-drop`; they get these DOM events (not bubbled):
 * over / leave while the finger moves, drop (detail = the mail) on release.
 */
export const TOUCH_OVER = 'zwc-mail-over';
export const TOUCH_LEAVE = 'zwc-mail-leave';
export const TOUCH_DROP = 'zwc-mail-drop';
/** on <body> while a touch drag runs (rows stop swiping, nothing gets selected) */
export const TOUCH_DRAGGING = 'mail-touch-dragging';

const HOLD_MS = 450;
/** finger movement before the hold that means "scroll", not "drag" */
const SLOP = 10;

/** Is HTML5 drag with a mouse/trackpad available? (a finger on iPad is not) */
export function canMouseDrag(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
}

/** Start watching a touch on a mail row; becomes a drag after a short hold. */
export function touchDragStart(e: ReactTouchEvent, mail: () => DraggedMail | null, label: string) {
  if (e.touches.length !== 1) return;
  const t0 = e.touches[0]!;
  const x0 = t0.clientX;
  const y0 = t0.clientY;
  let d: DraggedMail | null = null;
  let ghost: HTMLDivElement | null = null;
  let over: Element | null = null;

  const target = (x: number, y: number) => document.elementFromPoint(x, y)?.closest('[data-mail-drop]') ?? null;
  const place = (x: number, y: number) => {
    if (ghost) ghost.style.transform = `translate(${x + 12}px, ${y - 40}px)`;
  };
  const activate = () => {
    d = mail();
    if (!d) return cleanup();
    ghost = document.createElement('div');
    ghost.className = 'mail-drag-ghost';
    ghost.textContent = label;
    document.body.appendChild(ghost);
    document.body.classList.add(TOUCH_DRAGGING);
    place(x0, y0);
    navigator.vibrate?.(10);
  };
  const timer = window.setTimeout(activate, HOLD_MS);

  const move = (ev: TouchEvent) => {
    const t = ev.touches[0];
    if (!t) return;
    if (!ghost) {
      if (Math.abs(t.clientX - x0) > SLOP || Math.abs(t.clientY - y0) > SLOP) cleanup(); // a scroll
      return;
    }
    ev.preventDefault(); // dragging: no scroll
    place(t.clientX, t.clientY);
    const now = target(t.clientX, t.clientY);
    if (now !== over) {
      over?.dispatchEvent(new CustomEvent(TOUCH_LEAVE));
      now?.dispatchEvent(new CustomEvent(TOUCH_OVER));
      over = now;
    }
  };
  const end = (ev: TouchEvent) => {
    if (ghost) {
      ev.preventDefault(); // no click on the row after a drag
      over?.dispatchEvent(new CustomEvent(TOUCH_DROP, { detail: d }));
    }
    cleanup();
  };
  function cleanup() {
    window.clearTimeout(timer);
    over?.dispatchEvent(new CustomEvent(TOUCH_LEAVE));
    ghost?.remove();
    ghost = null;
    document.body.classList.remove(TOUCH_DRAGGING);
    document.removeEventListener('touchmove', move);
    document.removeEventListener('touchend', end);
    document.removeEventListener('touchcancel', cleanup);
  }
  document.addEventListener('touchmove', move, { passive: false });
  document.addEventListener('touchend', end, { passive: false });
  document.addEventListener('touchcancel', cleanup);
}
